import { Avatar, Theme } from '@mui/material';
import { useEffect, useState } from 'react';
import { useInView } from 'react-intersection-observer';

/** Named sizes for inline user avatars in labels (MUI-style: small | medium | large). */
export const userAvatarSizeMap = {
  small: 16,
  medium: 20,
  large: 24,
} as const;

/** Initials font size (px) tuned per named avatar size — smaller than half-size for a quieter fallback. */
export const userAvatarInitialsFontSizeMap = {
  small: 7,
  medium: 8,
  large: 10,
} as const;

export type UserAvatarSize = keyof typeof userAvatarSizeMap;

export type UserAvatarProps = {
  size?: UserAvatarSize;
  src?: string | null;
  initials: string;
};

/**
 * Circular user avatar: image when available, otherwise initials on a theme-aware grey circle.
 *
 * Image fetching is deferred until the avatar is near the viewport (avoids MUI Avatar's eager
 * `new Image()` preload for every row in long Autocomplete lists). We preload ourselves once
 * in view, and only pass `src` to MUI after the image has loaded — so initials stay visible
 * instead of an empty grey circle (or Person icon) while the photo is in flight. Passing `src`
 * only when cached also avoids MUI swapping children for a blank `<img>` mid-load.
 */
export function UserAvatar({ size = 'small', src, initials }: UserAvatarProps) {
  const pixelSize = userAvatarSizeMap[size];
  const fontSize = userAvatarInitialsFontSizeMap[size];
  const [imgFailed, setImgFailed] = useState(false);
  const [imgReady, setImgReady] = useState(false);
  // Observe this avatar; rootMargin starts loading slightly before it enters the scrollport.
  const { ref, inView } = useInView({
    triggerOnce: true,
    rootMargin: '100px',
  });

  // New URL → reset so we show initials again until the new image is ready.
  useEffect(() => {
    setImgFailed(false);
    setImgReady(false);
  }, [src]);

  // Preload only when near the viewport; gate MUI `src` on success so initials remain until then.
  useEffect(() => {
    if (!inView || !src || imgFailed) {
      return undefined;
    }

    let active = true;
    const image = new Image();
    image.onload = () => {
      if (active) {
        setImgReady(true);
      }
    };
    image.onerror = () => {
      if (active) {
        setImgFailed(true);
      }
    };
    image.src = src;

    return () => {
      active = false;
    };
  }, [inView, src, imgFailed]);

  const showImage = Boolean(src) && imgReady && !imgFailed;
  // Only pass src once cached — MUI then paints the photo without a blank loading gap.
  const resolvedSrc = showImage ? src! : undefined;

  return (
    <Avatar
      ref={ref}
      src={resolvedSrc}
      alt=""
      slotProps={{
        img: {
          loading: 'lazy',
          onError: () => {
            setImgFailed(true);
            setImgReady(false);
          },
        },
      }}
      sx={{
        width: pixelSize,
        height: pixelSize,
        // Soft grey that reads as a placeholder in both themes (same idea as ConnectionSelect).
        bgcolor: (theme: Theme) => (theme.palette.mode === 'dark' ? 'grey.800' : 'grey.200'),
        color: 'text.secondary',
        fontSize,
        lineHeight: 1,
        fontWeight: 'regular',
        letterSpacing: 0,
        // Tighter gap for the 16px size so the name sits closer to the avatar in captions.
        mr: size === 'small' ? 0.5 : 1,
        flexShrink: 0,
        display: 'inline-flex',
      }}
    >
      {initials}
    </Avatar>
  );
}
